import {
  collection,
  doc,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";

export interface WaitlistSubmission {
  email: string;
  name?: string;
  yardName?: string;
  horseCount?: number;
  timing?: string;
  message?: string;
  source: string;
  userId?: string;
}

export interface WaitlistResult {
  id: string;
  /** 1-indexed position in the waitlist. The first signup is #1. */
  position: number;
}

// Writes the waitlist doc + atomically increments a public counter at
// /meta/waitlist. The transaction protects against two simultaneous submits
// being assigned the same position. The counter doc is publicly readable
// so the success screen can show "Position #N"; per the rules it can
// only ever be incremented by 1 (no arbitrary writes).
export async function submitWaitlist(data: WaitlistSubmission): Promise<WaitlistResult> {
  const counterRef = doc(db, "meta", "waitlist");
  const waitlistRef = doc(collection(db, "waitlist"));

  const basePayload: Record<string, unknown> = {
    email: data.email.trim().toLowerCase(),
    source: data.source,
    createdAt: serverTimestamp(),
  };
  if (data.name) basePayload["name"] = data.name.trim();
  if (data.yardName) basePayload["yardName"] = data.yardName.trim();
  if (data.horseCount != null && !Number.isNaN(data.horseCount)) {
    basePayload["horseCount"] = data.horseCount;
  }
  if (data.timing) basePayload["timing"] = data.timing;
  if (data.message) basePayload["message"] = data.message.trim();
  if (data.userId) basePayload["userId"] = data.userId;

  const position = await runTransaction(db, async (tx) => {
    const counterSnap = await tx.get(counterRef);
    const current = counterSnap.exists() ? (counterSnap.data()["count"] as number) : 0;
    const next = current + 1;

    tx.set(counterRef, { count: next });
    tx.set(waitlistRef, { ...basePayload, position: next });

    return next;
  });

  return { id: waitlistRef.id, position };
}
